import { Language } from '@/types'
import { map, pipe, sortBy } from 'remeda'

import {
  FieldDefinition,
  FieldItem,
  OptionItem,
  SupportedKeys,
  TranslatedOptionItem,
} from '@/services/property'
import { parseFormulaConfig } from '@/utils/formula.config'
import {
  canonicalizeOptionItems,
  pickLabel,
  toSelectOptions,
} from '@/services/property/option.normalized'
import { parsePropertyFieldMetadata } from './property-field.metadata'
import { indexPropertyTranslationsByLang } from './property-field.translations'

export const toSupportedKey = ({
  id,
  field,
  lang,
}: {
  id?: string
  field: FieldDefinition
  lang: Language | undefined
}): SupportedKeys => {
  const parsedMetadata = parsePropertyFieldMetadata(field.metadata)
  const translationsByLang = indexPropertyTranslationsByLang(
    field.translations?.items,
  )
  // If its a default list, use the id as the value
  const value =
    field.type === 'LIST' && !field.value
      ? field.id
      : field.type === 'FORMULA'
        ? (parseFormulaConfig(field.value) ?? field.value)
        : field.value
  return {
    id: field.id,
    propertyKey: field.key,
    key: field.key,
    label: pickLabel(field.label, field.translations?.items, lang),
    baseLabel: field.label,
    value,
    listId: field.type === 'LIST' ? String(value ?? field.id) : undefined,
    type: field.type ?? 'TEXT',
    order: parsedMetadata.order ?? Number.POSITIVE_INFINITY,
    isMulti: parsedMetadata.isMulti,
    isRef: parsedMetadata.isRef,
    isID: parsedMetadata.isID,
    isPrivate: parsedMetadata.isPrivate,
    isLocked: parsedMetadata.isLocked,
    autoWeightUnit: parsedMetadata.autoWeightUnit,
    localizedValues: parsedMetadata.localizedValues,
    suffix: parsedMetadata.suffix,
    format: parsedMetadata.format,
    hideInInv: parsedMetadata.hideInInv,
    hideInRef: parsedMetadata.hideInRef,
    hideInRem: parsedMetadata.hideInRem,
    visibleInViews: parsedMetadata.visibleInViews,
    group: parsedMetadata.group,
    icon: parsedMetadata.icon,
    organizationID: field.organizationFieldsId,
    organizationName: field.organization?.name,
    propertyFieldsId: id,
    metadata: field.metadata,
    translations: field.translations,
    ...translationsByLang,
  }
}

type PropertyField = { id?: string; field: FieldDefinition }

export const toSupportedKeys = (
  data: PropertyField[],
  lang: Language | undefined,
): SupportedKeys[] => {
  try {
    const supportedKeys = data
      .filter(({ field }) => Boolean(field))
      .map(({ id, field }) => {
        return toSupportedKey({ id, field, lang })
      })
    return sortBy(supportedKeys, (sk) => sk?.order ?? Number.POSITIVE_INFINITY)
  } catch (error) {
    console.error(error)
    return []
  }
}

export const toOptionList = ({
  key,
  data,
  lang,
}: {
  key: string
  data?: FieldItem
  lang?: Language
}) => {
  const collator = new Intl.Collator(lang, {
    sensitivity: 'base',
    numeric: true,
  })
  const canonicalItems = canonicalizeOptionItems(data?.options?.items ?? [])

  const flattenItems = (
    items: TranslatedOptionItem[],
  ): TranslatedOptionItem[] =>
    items.flatMap((item) => [item, ...flattenItems(item.options ?? [])])

  const sortTranslatedOptions = (
    items: TranslatedOptionItem[],
  ): TranslatedOptionItem[] =>
    [...items]
      .map((item) =>
        item.options && item.options.length > 0
          ? {
              ...item,
              options: sortTranslatedOptions(item.options),
            }
          : item,
      )
      .sort((a, b) => collator.compare(a.label, b.label))

  const translateOption = (option: OptionItem): TranslatedOptionItem => {
    const { options: _ignored, ...base } = {
      ...option,
      label: pickLabel(option.label, option.translations?.items, lang),
    }

    const children = option.options?.items?.map(translateOption) ?? []

    return children.length > 0
      ? {
          ...base,
          options: children,
        }
      : {
          ...base,
        }
  }

  // 1. Translate and sort options
  const options: TranslatedOptionItem[] = pipe(
    canonicalItems,
    map(translateOption),
    sortTranslatedOptions,
  )

  const flatOptions = flattenItems(options)

  // 2. Build lookup maps (by key + AG Grid refData by id) in one traversal
  const optionsByKey: Record<string, TranslatedOptionItem> = {}
  const refDataById: Record<string, string> = {}

  const stack: TranslatedOptionItem[] = [...options]
  while (stack.length) {
    const current = stack.pop()
    if (!current) continue

    // Build refData by id
    const index = key === 'action' ? current.value : current.id
    if (index && current.label) {
      refDataById[index] = current.label
    }

    // Build lookup by key (fallback to id)
    const k = current.key ?? current.id
    if (k) optionsByKey[k] = current

    // Traverse nested options if any
    const children = current.options ?? []
    for (const child of children) stack.push(child)
  }

  const selectOptions = toSelectOptions(options, key)

  return {
    key,
    options,
    flatOptions,
    optionsByKey,
    refDataById,
    selectOptions,
    field: data,
    supportedKey: data ? toSupportedKey({ field: data, lang }) : undefined,
  }
}

export const toOptionListFromItems = ({
  key,
  items,
  lang,
}: {
  key: string
  items: OptionItem[]
  lang?: Language
}) => {
  return toOptionList({ key, data: { options: { items } } as FieldItem, lang })
}
